/**
 * Everything this build was configured with, read once and checked here.
 *
 * Two rules govern this module.
 *
 * First, **nothing here is secret**. Every value is compiled into the bundle,
 * which any visitor can read. A client secret in this file would be a
 * published client secret, which is why the sign-in flow uses PKCE and holds
 * no secret at all.
 *
 * Second, **a misconfiguration fails at startup, not at first use**. A missing
 * portal URL discovered when somebody clicks sign in is a support ticket; the
 * same mistake discovered when the application boots is a fixed deployment.
 */

/** A configuration value that was required and not supplied. */
export class ConfigurationError extends Error {
  constructor(variable: string, why: string) {
    super(`${variable} is not configured: ${why}`)
    this.name = 'ConfigurationError'
  }
}

function required(name: string, value: string, why: string): string {
  const trimmed = value?.trim() ?? ''
  if (!trimmed) throw new ConfigurationError(name, why)
  return trimmed
}

function optional(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? ''
  return trimmed || fallback
}

/** A flag, defaulting to on. Only an explicit "false" turns it off. */
function enabled(value: string | undefined, fallback = true): boolean {
  const trimmed = value?.trim().toLowerCase() ?? ''
  if (!trimmed) return fallback
  return trimmed !== 'false' && trimmed !== '0' && trimmed !== 'off'
}

/** Comma or space separated list, with the empties dropped. */
function list(value: string | undefined): readonly string[] {
  return (value ?? '')
    .split(/[,\s]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
}

/** A URL with any trailing slash removed, so joining a path is unambiguous. */
function base(value: string): string {
  return value.replace(/\/+$/, '')
}

const env = import.meta.env

export interface ApiConfig {
  /** Where the API lives. Empty means same origin, which is the deployed shape. */
  readonly baseUrl: string
}

export interface AuthConfig {
  /**
   * Whether anyone has to sign in at all.
   *
   * Off, the application runs with a fixed development identity and never
   * contacts the identity provider. This must match `AUTH_ENABLED` on the API:
   * turning it off here while the API still requires a token produces an
   * interface where every request is refused, which is a confusing way to
   * discover a mismatched setting.
   *
   * A production build refuses to start with this off.
   */
  readonly enabled: boolean
  readonly portalUrl: string
  readonly clientId: string
  readonly scopes: string
  readonly redirectUri: string
  readonly silentRedirectUri: string
  readonly postLogoutRedirectUri: string
}

export interface EmbeddingConfig {
  /**
   * Origins permitted to embed this application and exchange messages with it.
   *
   * An allowlist rather than a wildcard, in every environment. A parent frame
   * is not trustworthy by virtue of being the parent: without this, any page
   * that embeds this one could read the messages it sends.
   */
  readonly allowedParentOrigins: readonly string[]
}

export interface AppConfig {
  readonly api: ApiConfig
  readonly auth: AuthConfig
  readonly embedding: EmbeddingConfig
  readonly isDevelopment: boolean
  /**
   * Whether the account menu lets this page act as another account, while there
   * is no portal to sign in to. Development only: it needs authentication off,
   * is never on in a production build, and the API must have it on too.
   */
  readonly devAccounts: boolean
}

function readConfig(): AppConfig {
  const origin = typeof window === 'undefined' ? '' : window.location.origin

  const authEnabled = enabled(env.VITE_AUTH_ENABLED)

  if (!authEnabled && env.PROD) {
    // The same refusal the API makes, for the same reason: a development
    // shortcut that reaches a real deployment is not a shortcut, it is an
    // application with no access control.
    throw new ConfigurationError(
      'VITE_AUTH_ENABLED',
      'authentication cannot be switched off in a production build',
    )
  }

  // Only demanded when somebody is actually going to be signed in. Requiring
  // it with authentication off would mean naming an identity provider that is
  // deliberately never contacted.
  const portalUrl = authEnabled
    ? base(
        required(
          'VITE_PORTAL_URL',
          env.VITE_PORTAL_URL,
          'the application cannot sign anyone in without knowing its identity provider',
        ),
      )
    : base(optional(env.VITE_PORTAL_URL, ''))

  return {
    api: {
      // Same origin by default: the dev server proxies /api, and the deployed
      // setup serves both halves from one host. Both then behave alike, so a
      // cross-origin problem cannot appear in only one of them.
      baseUrl: base(optional(env.VITE_API_URL, '')),
    },
    auth: {
      enabled: authEnabled,
      portalUrl,
      clientId: authEnabled
        ? required(
            'VITE_OIDC_CLIENT_ID',
            env.VITE_OIDC_CLIENT_ID,
            'the identity provider needs to know which registered client is asking',
          )
        : optional(env.VITE_OIDC_CLIENT_ID, 'development'),
      scopes: optional(env.VITE_OIDC_SCOPES, 'openid profile email dmbuilder-api'),
      redirectUri: `${origin}/callback`,
      silentRedirectUri: `${origin}/silentrenew`,
      postLogoutRedirectUri: origin,
    },
    embedding: {
      allowedParentOrigins: list(env.VITE_ALLOWED_PARENT_ORIGINS),
    },
    isDevelopment: env.DEV,
    devAccounts: !authEnabled && !env.PROD && enabled(env['VITE_DEV_ACCOUNT_SWITCH'], false),
  }
}

export const config: AppConfig = readConfig()
